import React, { useCallback, useState } from "react"

import {
  useHumanReviewProjectMutationsTransport,
  useHumanReviewProjectsTransport,
  useHumanReviewProjectWithSimulationIdsFetcherTransport,
  useProjectAssignmentEmailTransport,
} from "@/human-feedback/transport"
import {
  Button,
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverTitle,
  PopoverTrigger,
  toast,
} from "@coval/ui"
import { Users } from "lucide-react"

import { logger } from "@/lib/logger"

const log = logger("AddToHumanReviewButton")

interface AddToHumanReviewButtonProps {
  simulationOutputIds: string[]
  disabled?: boolean
  title?: string
}

export const AddToHumanReviewButton: React.FC<AddToHumanReviewButtonProps> = ({
  simulationOutputIds,
  disabled = false,
  title,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  // Human review project queries and mutations
  const { data: humanReviewProjects } = useHumanReviewProjectsTransport()
  const fetchProjectWithSimulationIds =
    useHumanReviewProjectWithSimulationIdsFetcherTransport()
  const { updateProject } = useHumanReviewProjectMutationsTransport()

  // Email notification mutation
  const { mutateAsync: sendProjectUpdate } =
    useProjectAssignmentEmailTransport()

  const handleAddToHumanReviewProject = useCallback(
    async (projectId: string) => {
      if (!simulationOutputIds.length) {
        toast.danger({
          title: "Error",
          description: "No simulation outputs available to add to project",
        })
        return
      }

      try {
        const currentProject = await fetchProjectWithSimulationIds(projectId)

        // Check which simulation outputs are already in the project
        const existingSimulationIds = currentProject.linked_simulation_ids || []
        const newSimulationIds = simulationOutputIds.filter(
          (id) => !existingSimulationIds.includes(id),
        )

        if (newSimulationIds.length === 0) {
          toast.success({
            title: "Already Added",
            description: `All simulation outputs are already in "${currentProject.name}".`,
          })
          setIsMenuOpen(false)
          return
        }

        // Add the new simulation outputs to the project's linked simulation IDs
        const updatedSimulationOutputs = [
          ...existingSimulationIds,
          ...newSimulationIds,
        ]

        await updateProject({
          id: projectId,
          simulationOutputs: updatedSimulationOutputs,
        })

        toast.success({
          title: "Success",
          description: `Added ${newSimulationIds.length} simulation${newSimulationIds.length === 1 ? "" : "s"} to "${currentProject.name}".`,
        })
        setIsMenuOpen(false)

        // Send email notification to all assignees automatically
        // Don't await - send in background
        sendProjectUpdate({
          projectId,
          isNewProject: false, // This is an update to existing project
          // Only pass simulationOutputId if there's exactly one simulation output
          simulationOutputId:
            newSimulationIds.length === 1 ? newSimulationIds[0] : undefined,
        }).catch((error) => {
          log.error("Failed to send email notifications", error)
          // Don't show error to user - email failure shouldn't block the workflow
        })
      } catch (error) {
        log.error("Error adding to human review project", error)
        toast.error({
          title: "Failed to add to project",
          error,
          fallback: "Could not add the selected simulations. Please try again.",
        })
      }
    },
    [
      simulationOutputIds,
      fetchProjectWithSimulationIds,
      updateProject,
      sendProjectUpdate,
    ],
  )

  // Don't render if no simulation outputs or no projects available
  if (!simulationOutputIds.length || !humanReviewProjects?.length) {
    return null
  }

  return (
    <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          disabled={disabled}
          title={title ?? "Add all simulated conversations to human review"}
          aria-label="Run human review"
          data-testid="run-add-to-human-review-button"
        >
          <Users />
          Human Review
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-[220px]">
        <PopoverTitle>
          Add {simulationOutputIds.length} simulation
          {simulationOutputIds.length === 1 ? "" : "s"} to:
        </PopoverTitle>
        {humanReviewProjects.slice(0, 10).map((project) => (
          <PopoverItem
            key={project.ulid}
            className="text-left"
            data-project-name={project.name}
            data-testid={`run-add-to-human-review-option-${project.ulid}`}
            onClick={() => {
              void handleAddToHumanReviewProject(project.ulid)
            }}
          >
            <div className="flex flex-col flex-1 min-w-0">
              <span className="text-sm font-medium truncate">
                {project.name.length > 20
                  ? `${project.name.slice(0, 20)}...`
                  : project.name}
              </span>
              {project.description && (
                <span className="text-xs text-coval-text-muted truncate">
                  {project.description.length > 20
                    ? `${project.description.slice(0, 20)}...`
                    : project.description}
                </span>
              )}
            </div>
          </PopoverItem>
        ))}
      </PopoverContent>
    </Popover>
  )
}
